import React, { useState, useRef } from 'react';
import ConsentDocuments from '../ConsentDocuments';
import { isDocumentShared } from '../useDocumentSharing';
import {
  Box, Typography, IconButton, Button, TextField, InputAdornment,
  Stack, Chip, Menu, MenuItem, Tooltip, CircularProgress, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LinkIcon from '@mui/icons-material/Link';
import AlertDialog from '../../../Dialog.js';

const AVAILABLE_TAGS = [
  'Medical Records', 'Lab Results', 'Treatment', 'Plan', 'History',
  'Blood Test', 'Imaging', 'Prescriptions', 'Consent Forms', 'Insurance',
  'Session Notes', 'Generated', 'Consultation', 'Summary', 'Other',
];

const PatientDocuments = ({
  patientId,
  historyItems = [],
  currentUserId,
  isAdminUser = false,
  handleViewItem,
  handleDeleteItem,
  handleDownloadReport,
  handlePatientFileUpload,
  convertToGMT8Date,
  setAlert,
  setOpenDialog,
  openDialog,
  isUploading,
  onPreviewItem,
  selectedItems = [],
  toggleItemSelection,
  clearAllSelections,
  deleteSelectedHistoryItems,
  handleItemShare,
  isMindvaultLinked = false,
  isMindvaultLinkLoading = false,
}) => {
  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editingDocName, setEditingDocName] = useState('');
  const [uploadDropdownAnchor, setUploadDropdownAnchor] = useState(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const dragDepthRef = useRef(0);

  const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleFilesDragEnter = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    if (isSelectMode || isUploading) return;
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleFilesDragOver = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = isSelectMode || isUploading ? 'none' : 'copy';
  };

  const handleFilesDragLeave = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragActive(false);
  };

  const handleFilesDrop = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    if (isSelectMode || isUploading) return;

    const files = Array.from(event.dataTransfer.files || []);
    if (files.length > 0) handlePatientFileUpload({ target: { files } });
  };

  const handleMassDelete = () => {
    setOpenDialog({
      open: true,
      content: `Are you sure you want to delete ${selectedItems.length} item(s)? This cannot be undone.`,
      confirmAction: async () => {
        setIsDeleting(true);
        try {
          await deleteSelectedHistoryItems();
          setIsSelectMode(false);
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    clearAllSelections();
  };

  // Filter to show documents, attachments, and consultations
  // Admins see all files; non-admins only see their own (null = legacy, show for backward compat)
  const documentItems = historyItems.filter(
    (item) =>
      (item.type === 'Document' || item.type === 'Attachment' || item.type === 'Consultation') &&
      (isAdminUser || !item.createdByUserId || item.createdByUserId === currentUserId)
  );

  // Apply search, filter, and sort
  const filteredDocuments = documentItems
    .filter((doc) => {
      if (searchQuery) {
        const title = (doc.title || '').toLowerCase();
        if (!title.includes(searchQuery.toLowerCase())) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date') {
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else if (sortBy === 'name') {
        comparison = (a.title || '').localeCompare(b.title || '');
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    handleSortClose();
  };

  const startRename = (doc) => {
    setEditingDocId(doc.id);
    setEditingDocName(doc.title || '');
  };

  const cancelRename = () => {
    setEditingDocId(null);
    setEditingDocName('');
  };

  const getDocTypeChip = (doc) => {
    if (doc.type === 'Consultation') {
      return (
        <Chip
          label="Transcript"
          size="small"
          sx={{
            backgroundColor: '#E5E7EB',
            color: '#6B7280',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      );
    }
    if (doc.type === 'Document') {
      const isGenerated = doc.originalData?.templateId;
      return (
        <Chip
          label={isGenerated ? 'Generated' : 'Document'}
          size="small"
          sx={{
            backgroundColor: isGenerated ? '#EDE9FE' : '#DCFCE7',
            color: isGenerated ? '#7C3AED' : '#16A34A',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      );
    }
    return (
      <Chip
        label="Attachment"
        size="small"
        sx={{
          backgroundColor: '#FEF3C7',
          color: '#D97706',
          fontWeight: 600,
          fontSize: '0.7rem',
        }}
      />
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <ConsentDocuments key={patientId} patientId={patientId} />
      {/* Documents Header */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Title and Upload */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
            Files ({filteredDocuments.length})
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {!isSelectMode ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<PlaylistAddCheckIcon />}
                  onClick={() => setIsSelectMode(true)}
                  sx={{
                    borderColor: '#D1D5DB',
                    color: '#374151',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#29C0BB', backgroundColor: '#F0FDFA' },
                  }}
                >
                  Select Items
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    backgroundColor: '#29C0BB',
                    '&:hover': { backgroundColor: '#1ca8a4' },
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                  }}
                >
                  Upload File
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                  disabled={isDeleting}
                  onClick={handleMassDelete}
                  sx={{
                    backgroundColor: '#DC2626',
                    '&:hover': { backgroundColor: '#B91C1C' },
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    boxShadow: 'none',
                  }}
                >
                  {isDeleting ? 'Deleting…' : `Delete Selected (${selectedItems.length})`}
                </Button>
                <Button
                  variant="outlined"
                  onClick={exitSelectMode}
                  sx={{
                    borderColor: '#D1D5DB',
                    color: '#374151',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#9CA3AF' },
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onClick={(e) => { e.target.value = null; }}
            onChange={handlePatientFileUpload}
          />
        </Box>

        {/* Search and Sort Controls */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#F9FAFB',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {/* Search */}
          <TextField
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                '&:hover fieldset': { borderColor: '#29C0BB' },
                '&.Mui-focused fieldset': { borderColor: '#29C0BB' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Sort Button */}
          <Button
            variant="outlined"
            startIcon={<SortIcon />}
            onClick={handleSortClick}
            sx={{
              borderColor: '#D1D5DB',
              color: '#374151',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { borderColor: '#29C0BB', backgroundColor: '#F0FDFA' },
            }}
          >
            Sort
          </Button>
          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={handleSortClose}
            PaperProps={{ sx: { borderRadius: '8px', minWidth: 160 } }}
          >
            <MenuItem
              onClick={() => handleSortSelect('date')}
              sx={{ color: sortBy === 'date' ? '#29C0BB' : '#374151', fontWeight: sortBy === 'date' ? 600 : 400 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                Date
                {sortBy === 'date' && (sortOrder === 'desc' ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowUpIcon fontSize="small" />)}
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => handleSortSelect('name')}
              sx={{ color: sortBy === 'name' ? '#29C0BB' : '#374151', fontWeight: sortBy === 'name' ? 600 : 400 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                Name
                {sortBy === 'name' && (sortOrder === 'desc' ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowUpIcon fontSize="small" />)}
              </Box>
            </MenuItem>
          </Menu>
        </Box>

        {/* Document List */}
        <Box
          onDragEnter={handleFilesDragEnter}
          onDragOver={handleFilesDragOver}
          onDragLeave={handleFilesDragLeave}
          onDrop={handleFilesDrop}
          sx={{ position: 'relative', minHeight: 120 }}
        >
          <Box
            sx={{
              mx: 2,
              mt: 2,
              mb: filteredDocuments.length === 0 ? 0 : 1,
              minHeight: 68,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              border: '1px dashed #9CA3AF',
              borderRadius: '8px',
              backgroundColor: '#FCFDFD',
              color: '#6B7280',
              textAlign: 'center',
            }}
          >
            <UploadIcon sx={{ color: '#29C0BB' }} />
            <Typography variant="body2">
              Drag and drop files here, or click <Box component="span" sx={{ color: '#148F8B', fontWeight: 600 }}>Upload File</Box> above
            </Typography>
          </Box>
          {filteredDocuments.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <DescriptionIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 1.5 }} />
              <Typography variant="body1" sx={{ color: '#6B7280' }}>
                No documents found
              </Typography>
              <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
                Upload files to get started using the drop area above
              </Typography>
            </Box>
          ) : (
            filteredDocuments.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid #F3F4F6',
                  '&:hover': { backgroundColor: '#F9FAFB' },
                  transition: 'background-color 0.15s',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (editingDocId !== doc.id) {
                    if (onPreviewItem) onPreviewItem(doc);
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  {/* Document Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Name Row */}
                    {editingDocId === doc.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TextField
                          value={editingDocName}
                          onChange={(e) => setEditingDocName(e.target.value)}
                          size="small"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              '&.Mui-focused fieldset': { borderColor: '#29C0BB' },
                            },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); cancelRename(); }}
                          sx={{ color: '#EF4444' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {isSelectMode && (
                          <Checkbox
                            size="small"
                            checked={selectedItems.includes(doc.id)}
                            onChange={(e) => { e.stopPropagation(); toggleItemSelection(doc.id); }}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0, '&.Mui-checked': { color: '#29C0BB' } }}
                          />
                        )}
                        <DescriptionIcon sx={{ color: '#29C0BB', fontSize: 20, flexShrink: 0 }} />
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {doc.title || 'Untitled'}
                        </Typography>
                      </Box>
                    )}

                    {/* Metadata Row */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 3.5 }}>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        {convertToGMT8Date ? convertToGMT8Date(doc.createdAt) : new Date(doc.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#D1D5DB' }}>|</Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        By: {doc.createdBy || 'Unknown'}
                      </Typography>
                      <Box sx={{ ml: 0.5 }}>{getDocTypeChip(doc)}</Box>
                    </Stack>
                  </Box>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onPreviewItem) onPreviewItem(doc);
                        }}
                        sx={{ color: '#6B7280', '&:hover': { color: '#29C0BB', backgroundColor: '#F0FDFA' } }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(doc.type === 'Document' || doc.type === 'Consultation') && (
                      <Tooltip
                        title={
                          isMindvaultLinkLoading
                            ? 'Checking MindVault connection…'
                            : !isMindvaultLinked
                              ? 'Connect with this client on MindVault to share'
                              : isDocumentShared(doc)
                                ? (doc.share_expires_at ? `Shared until ${new Date(doc.share_expires_at).toLocaleString()}` : 'Shared with client on MindVault')
                                : (doc.isShared && doc.share_expires_at ? 'Sharing expired. Share again to restore access.' : 'Not shared with client on MindVault')
                        }
                      >
                        {/* span keeps the tooltip working while the button is disabled */}
                        <span>
                          <IconButton
                            data-tour={doc.id === 'tour-report' ? 'demo-share' : undefined}
                            aria-label={isDocumentShared(doc) ? 'Revoke sharing with client on MindVault' : 'Share with client on MindVault'}
                            size="small"
                            disabled={isMindvaultLinkLoading || !isMindvaultLinked}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (handleItemShare) handleItemShare(doc);
                            }}
                            sx={{
                              color: isMindvaultLinked && isDocumentShared(doc) ? '#10B981' : '#9CA3AF',
                              '&.Mui-disabled': { color: '#D1D5DB' },
                              '&:hover': isDocumentShared(doc)
                                ? { color: '#059669', backgroundColor: '#D1FAE5' }
                                : { color: '#29C0BB', backgroundColor: '#F0FDFA' },
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDialog({
                            open: true,
                            content: `Are you sure you want to delete "${doc.title || 'this document'}"?`,
                            confirmAction: () => handleDeleteItem(doc),
                          });
                        }}
                        sx={{ color: '#6B7280', '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Box>
            ))
          )}
          {isDragActive && !isSelectMode && !isUploading && (
            <Box
              aria-live="polite"
              sx={{
                position: 'absolute',
                inset: 12,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #29C0BB',
                borderRadius: '10px',
                backgroundColor: 'rgba(240, 253, 250, 0.94)',
                color: '#148F8B',
                fontWeight: 600,
                pointerEvents: 'none',
              }}
            >
              Drop files here to upload
            </Box>
          )}
        </Box>
      </Box>

      {openDialog && (
        <AlertDialog
          data={{
            open: openDialog.open,
            content: openDialog.content,
            confirmAction: openDialog.confirmAction,
            setOpen: setOpenDialog,
          }}
        />
      )}
    </Box>
  );
};

export default PatientDocuments;
